import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import NotebookMockup from "@/components/NotebookMockup";
import WaveBackground from "@/components/WaveBackground";
import SectionBadge from "@/components/SectionBadge";

const Hero = ({ onRequestDemo, onScrollToSolutions }) => {
  const { t } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-[84px] md:pt-[96px] lg:pt-[104px] pb-16 lg:pb-24 overflow-hidden"
    >
      {/* Background grid + glow + wave network */}
      <div aria-hidden className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <WaveBackground opacity={0.55} />
      <div
        aria-hidden
        className="absolute top-1/3 -left-32 w-[520px] h-[520px] glow-radial pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -top-10 right-0 w-[600px] h-[600px] glow-radial opacity-60 pointer-events-none"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="hero-badge"
            >
              <SectionBadge>{t.hero.badge}</SectionBadge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-[54px] xl:text-[60px] font-bold leading-[1.05] tracking-tight text-white text-balance pr-2"
              data-testid="hero-title"
            >
              {t.hero.titleStart}{" "}
              <span className="relative inline-block">
                <span className="text-[#1dc7e9]">{t.hero.titleAccent}.</span>
                <span
                  aria-hidden
                  className="absolute -inset-x-2 -inset-y-1 -z-10 blur-2xl bg-[#1dc7e9]/25 rounded-full"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base sm:text-lg text-[#b8c0cc] max-w-xl leading-relaxed"
              data-testid="hero-description"
            >
              {t.hero.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <button
                onClick={onRequestDemo}
                className="btn-primary inline-flex items-center justify-center gap-2 bg-[#1dc7e9] text-[#04060d] font-bold text-sm px-6 py-3.5 rounded-lg hover:shadow-[0_0_36px_rgba(29,199,233,0.5)] hover:-translate-y-0.5 transition-all duration-300"
                data-testid="hero-cta-primary"
              >
                {t.hero.ctaPrimary}
                <ArrowRight size={16} />
              </button>
              <button
                onClick={onScrollToSolutions}
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#1dc7e9]/30 text-white font-medium text-sm px-6 py-3.5 rounded-lg hover:bg-[#1dc7e9]/10 hover:border-[#1dc7e9]/60 transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                {t.hero.ctaSecondary}
              </button>
            </motion.div>

            {/* Metrics */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-12 grid grid-cols-3 gap-4 sm:gap-8 max-w-xl"
              data-testid="hero-metrics"
            >
              {t.hero.metrics.map((m, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-1 border-l border-[#1dc7e9]/15 pl-4"
                  data-testid={`hero-metric-${i}`}
                >
                  <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                    {m.value}
                  </div>
                  <div className="text-[11px] text-[#b8c0cc] leading-snug">
                    {m.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right - notebook */}
          <div className="lg:col-span-6 relative">
            <NotebookMockup />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
